/**
 * Video validation utilities for client-side checks
 */

export interface VideoValidationResult {
    isValid: boolean;
    error?: string;
    details?: {
        width?: number;
        height?: number;
        maxWidth?: number;
        maxHeight?: number;
    };
}

const MAX_VIDEO_WIDTH = 1920;
const MAX_VIDEO_HEIGHT = 1080;
const MAX_FILE_SIZE_MB = 750;


export const validateVideoResolution = (file: File): Promise<VideoValidationResult> => {
    return new Promise((resolve) => {
        // Check file size first
        const fileSizeMB = file.size / (1024 * 1024);
        if (fileSizeMB > MAX_FILE_SIZE_MB) {
            resolve({
                isValid: false,
                error: `File size (${fileSizeMB.toFixed(1)}MB) exceeds maximum limit of ${MAX_FILE_SIZE_MB}MB`,
            });
            return;
        }

        // Check if it's a video file
        if (!file.type.startsWith('video/')) {
            resolve({
                isValid: false,
                error: 'File must be a video',
            });
            return;
        }

        // Create video element to read metadata
        const video = document.createElement('video');
        video.preload = 'metadata';

        video.onloadedmetadata = () => {
            // Clean up
            window.URL.revokeObjectURL(video.src);

            const width = video.videoWidth;
            const height = video.videoHeight;

            if (width > MAX_VIDEO_WIDTH || height > MAX_VIDEO_HEIGHT) {
                resolve({
                    isValid: false,
                    error: `Video resolution (${width}x${height}) exceeds maximum supported resolution of ${MAX_VIDEO_WIDTH}x${MAX_VIDEO_HEIGHT}. Please upload a video with Full HD resolution.`,
                    details: {
                        width,
                        height,
                        maxWidth: MAX_VIDEO_WIDTH,
                        maxHeight: MAX_VIDEO_HEIGHT,
                    },
                });
                return;
            }

            // Resolution is valid
            resolve({
                isValid: true,
                details: {
                    width,
                    height,
                    maxWidth: MAX_VIDEO_WIDTH,
                    maxHeight: MAX_VIDEO_HEIGHT,
                },
            });
        };

        video.onerror = () => {
            // Clean up
            window.URL.revokeObjectURL(video.src);
            resolve({
                isValid: true,
            });
        };

        // Load the video file
        video.src = URL.createObjectURL(file);
    });
};

/**
 * Format resolution for display
 */
export const formatResolution = (width: number, height: number): string => {
    return `${width}x${height}`;
};

/**
 * Get resolution class name (SD, HD, FHD, etc.)
 */
export const getResolutionClass = (width: number, height: number): string => {
    const totalPixels = width * height;

    if (totalPixels >= 7680 * 4320) return '8K';
    if (totalPixels >= 3840 * 2160) return '4K';
    if (totalPixels >= 2560 * 1440) return 'QHD';
    if (totalPixels >= 1920 * 1080) return 'FHD';
    if (totalPixels >= 1280 * 720) return 'HD';
    return 'SD';
};
